import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { handleActions } from 'redux-actions';
import { getDreamupPrompt } from '@wix/da-shared-react/pkg/types/deviation/getters';
import { dreamupGenerationFailed } from '@wix/da-shared-react/pkg/redux/dreamup/actions';
import { reduxKey } from './constants';
import { ArtStyle, SubmissionStatus, VariationSource } from './types';
import { changeArtStyle, changeAspectRatio, changeNegativePrompt, changePrompt, changePromptStrength, changeSimilarity, clearPrompt, clearVariation, generateFromPrompt, generationFromPromptInitiationFailed, generationFromPromptInitiationSucceeded, hidePromptSettingsPanel, hideStartingImageSelector, initializeForm, showPromptSettingsPanel, showStartingImageSelector, startVariation, toggleNegativePrompt, updateRestrictions } from './actions';
const defaultFormValues = {
    aspectRatio: '1:1',
    similarity: 50,
    prompt: '',
    promptStrength: 7,
    artStyle: ArtStyle.Default,
    isNegativePromptInputVisible: false,
    negativePrompt: ''
};
const reducers = handleActions({
    [`${initializeForm}`]: (state, { payload: { data, options } })=>{
        var _data_defaultFormValues, _data_defaultFormValues1;
        return _object_spread_props(_object_spread({}, state), {
            restrictions: data.restrictions,
            pointsPackages: data.pointsPackages.map((pointsPackage)=>({
                    points: pointsPackage.pricePoints,
                    price: pointsPackage.priceDollars,
                    subproductId: pointsPackage.subproductId
                })),
            isPromptSettingsPanelVisible: options.expandSettings,
            shouldPullFormValuesFromLocalStorage: options.persistFromStorage,
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                // The only default returned by the backend is `promptStrength`.
                // (Backend calls it `guidance_scale`, if you need to find it)
                promptStrength: ((_data_defaultFormValues = data.defaultFormValues) === null || _data_defaultFormValues === void 0 ? void 0 : _data_defaultFormValues.promptStrength) ? (_data_defaultFormValues1 = data.defaultFormValues) === null || _data_defaultFormValues1 === void 0 ? void 0 : _data_defaultFormValues1.promptStrength : state.formValues.promptStrength,
                prompt: options.initialPrompt
            })
        });
    },
    [`${changePrompt}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                prompt: action.payload.prompt
            })
        }),
    [`${toggleNegativePrompt}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                negativePrompt: '',
                isNegativePromptInputVisible: !state.formValues.isNegativePromptInputVisible
            })
        }),
    [`${changeNegativePrompt}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                negativePrompt: action.payload.negativePrompt
            })
        }),
    [`${clearPrompt}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                prompt: ''
            })
        }),
    [`${changeSimilarity}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                similarity: action.payload.similarity
            })
        }),
    [`${changePromptStrength}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                promptStrength: action.payload.promptStrength
            })
        }),
    [`${changeAspectRatio}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                aspectRatio: action.payload.aspectRatio
            })
        }),
    [`${changeArtStyle}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                artStyle: action.payload.artStyle
            })
        }),
    [`${startVariation}`]: (state, action)=>{
        var _getDreamupPrompt;
        return _object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                // Our AI does not support alternate aspect ratios for variations
                aspectRatio: defaultFormValues.aspectRatio,
                prompt: action.payload.options.variationSource === VariationSource.Dreamup ? (_getDreamupPrompt = getDreamupPrompt(action.payload.deviation)) !== null && _getDreamupPrompt !== void 0 ? _getDreamupPrompt : '' : state.formValues.prompt
            }),
            variationTarget: action.payload.deviation,
            variationSource: action.payload.options.variationSource
        });
    },
    [`${clearVariation}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, state.formValues), {
                prompt: state.variationSource === VariationSource.Dreamup ? '' : state.formValues.prompt
            }),
            variationTarget: undefined,
            variationSource: undefined
        }),
    [`${showPromptSettingsPanel}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            isPromptSettingsPanelVisible: true
        }),
    [`${hidePromptSettingsPanel}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            isPromptSettingsPanelVisible: false
        }),
    [`${generateFromPrompt}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            currentSubmission: {
                status: SubmissionStatus.AwaitingResponse
            }
        }),
    [`${generationFromPromptInitiationSucceeded}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            formValues: _object_spread_props(_object_spread({}, defaultFormValues), {
                prompt: state.formValues.prompt,
                artStyle: state.formValues.artStyle,
                promptStrength: state.formValues.promptStrength,
                aspectRatio: state.formValues.aspectRatio,
                negativePrompt: state.formValues.negativePrompt,
                isNegativePromptInputVisible: state.formValues.isNegativePromptInputVisible
            }),
            variationTarget: state.variationTarget,
            currentSubmission: {
                status: SubmissionStatus.Idle
            },
            restrictions: _object_spread({}, action.payload.restrictions)
        }),
    [`${generationFromPromptInitiationFailed}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            currentSubmission: {
                status: SubmissionStatus.Error,
                error: action.payload.error,
                serverErrorMessage: action.payload.serverErrorMessage
            }
        }),
    [`${showStartingImageSelector}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            isStartingImageSelectorOpen: true
        }),
    [`${hideStartingImageSelector}`]: (state)=>_object_spread_props(_object_spread({}, state), {
            isStartingImageSelectorOpen: false
        }),
    [`${dreamupGenerationFailed}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            restrictions: _object_spread_props(_object_spread({}, state.restrictions), {
                freePromptsRemaining: action.payload.freePromptsRemaining
            })
        }),
    [`${updateRestrictions}`]: (state, action)=>_object_spread_props(_object_spread({}, state), {
            restrictions: action.payload.restrictions
        })
}, {
    currentSubmission: {
        status: SubmissionStatus.Idle
    },
    formValues: defaultFormValues,
    isPromptSettingsPanelVisible: false,
    shouldPullFormValuesFromLocalStorage: false,
    restrictions: {
        // Mostly gibberish defaults so that types are easier to work with. This should get overridden by
        // the initializeDreamup action. Erring toward restrictive defaults as a safety measure.
        canUseFreePrompt: false,
        canUsePaidPrompt: false,
        freePromptsAllowed: 0,
        freePromptsRemaining: 0,
        imagesPerPrompt: 0,
        promptPointsPrice: 40,
        queuePriority: 'low'
    },
    pointsPackages: [],
    isStartingImageSelectorOpen: false
});
export default {
    [reduxKey]: reducers
};

//# sourceMappingURL=reducer.js.map